import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { billingAPI, formatApiError } from '@/lib/api';
import { Eye, Settings as SettingsIcon, LogOut, CreditCard, User, Loader2, Check, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <header className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-md border-b border-zinc-200">
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
        <Link to="/dashboard" className="flex items-center gap-2">
          <Eye className="w-6 h-6 text-brand" strokeWidth={2} />
          <span className="font-heading font-black text-lg tracking-tight text-zinc-900">ContractEye</span>
        </Link>
        <div className="flex items-center gap-4">
          <span className="font-body text-sm text-zinc-500 hidden sm:block">{user?.email}</span>
          <Link to="/settings" className="p-2 hover:bg-zinc-100 transition-colors"><SettingsIcon className="w-4 h-4 text-zinc-600" /></Link>
          <button onClick={async () => { await logout(); navigate('/'); }} className="p-2 hover:bg-zinc-100 transition-colors" data-testid="settings-logout-btn"><LogOut className="w-4 h-4 text-zinc-600" /></button>
        </div>
      </div>
    </header>
  );
}

export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [pollingStatus, setPollingStatus] = useState(null);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');

  // Poll for payment status after Stripe redirect
  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 8;
    if (attempts >= maxAttempts) {
      setPollingStatus('timeout');
      return;
    }
    try {
      const res = await billingAPI.checkoutStatus(sessionId);
      if (res.data.payment_status === 'paid') {
        setPollingStatus('success');
        toast.success('Payment successful! Welcome to Pro!');
        await refreshUser();
        return;
      }
      if (res.data.status === 'expired') {
        setPollingStatus('expired');
        return;
      }
      setPollingStatus('processing');
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2500);
    } catch {
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2500);
      } else {
        setPollingStatus('error');
      }
    }
  }, [refreshUser]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      setPollingStatus('processing');
      pollPaymentStatus(sessionId);
      // Clean URL
      window.history.replaceState({}, '', '/settings');
    }
  }, [searchParams, pollPaymentStatus]);

  const handleUpgrade = async (plan = 'pro') => {
    setUpgradeLoading(true);
    try {
      const origin = window.location.origin;
      const res = await billingAPI.createCheckout({ origin_url: origin, plan });
      if (res.data.upgraded) {
        // Inline upgrade — no redirect needed
        toast.success(res.data.message || 'Subscription updated!');
        await refreshUser();
      } else if (res.data.url) {
        // New subscription — redirect to Stripe
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUpgradeLoading(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      const origin = window.location.origin;
      const res = await billingAPI.createPortalSession({ origin_url: origin });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const planType = user?.plan_type || 'free';
  const isPro = user?.subscription_status === 'active' && (planType === 'pro' || planType === 'pro_plus');
  const isProPlus = user?.subscription_status === 'active' && planType === 'pro_plus';
  const planLabel = isProPlus ? 'Pro Plus' : isPro ? 'Pro' : 'Free';

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="settings-page">
      <Navbar />
      <main className="pt-24 pb-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto">
          <h1 className="font-heading font-bold text-2xl tracking-tight text-zinc-900 mb-8">Settings</h1>

          {/* Payment status notification */}
          {pollingStatus === 'processing' && (
            <div className="bg-blue-50 border border-blue-200 px-4 py-3 mb-6 flex items-center gap-3" data-testid="payment-processing">
              <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
              <p className="font-body text-sm text-blue-800">Processing your payment...</p>
            </div>
          )}
          {pollingStatus === 'success' && (
            <div className="bg-emerald-50 border border-emerald-200 px-4 py-3 mb-6 flex items-center gap-3" data-testid="payment-success">
              <Check className="w-4 h-4 text-emerald-600" />
              <p className="font-body text-sm text-emerald-800">Payment successful! You are now on the Pro plan.</p>
            </div>
          )}
          {(pollingStatus === 'error' || pollingStatus === 'timeout') && (
            <div className="bg-amber-50 border border-amber-200 px-4 py-3 mb-6 flex items-center gap-3" data-testid="payment-error">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <p className="font-body text-sm text-amber-800">We couldn't confirm your payment. If you were charged, your plan will update shortly.</p>
            </div>
          )}

          {/* Subscription Card */}
          <div className="bg-white border border-zinc-200 p-6 mb-6" data-testid="subscription-card">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="w-4 h-4 text-zinc-500" />
              <h2 className="font-heading font-bold text-base text-zinc-900">Subscription</h2>
            </div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="font-body text-sm text-zinc-700">Current Plan: <span className="font-bold">{planLabel}</span></p>
                {isPro && user?.subscription_period_end && (
                  <p className="font-body text-xs text-zinc-500 mt-1">
                    Active until {new Date(user.subscription_period_end).toLocaleDateString()}
                  </p>
                )}
              </div>
              {isProPlus ? (
                <span className="bg-brand text-white border border-brand px-3 py-1 text-xs font-bold uppercase tracking-wider" data-testid="plan-pro-plus-badge">Pro Plus</span>
              ) : isPro ? (
                <span className="bg-emerald-100 text-emerald-700 border border-emerald-200 px-3 py-1 text-xs font-bold uppercase tracking-wider" data-testid="plan-active-badge">Pro</span>
              ) : (
                <span className="bg-zinc-100 text-zinc-600 border border-zinc-200 px-3 py-1 text-xs font-bold uppercase tracking-wider" data-testid="plan-free-badge">Free</span>
              )}
            </div>

            {!isPro && !isProPlus ? (
              <div className="space-y-4 mb-4">
                <div className="flex items-center justify-center gap-3 py-2" data-testid="settings-billing-toggle">
                  <span className={`font-body text-xs font-medium ${billingCycle === 'monthly' ? 'text-zinc-900' : 'text-zinc-400'}`}>Monthly</span>
                  <button onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')} className={`relative w-10 h-5 transition-colors ${billingCycle === 'annual' ? 'bg-brand' : 'bg-zinc-300'}`}>
                    <span className={`absolute top-0.5 w-4 h-4 bg-white transition-transform ${billingCycle === 'annual' ? 'left-5' : 'left-0.5'}`} />
                  </button>
                  <span className={`font-body text-xs font-medium ${billingCycle === 'annual' ? 'text-zinc-900' : 'text-zinc-400'}`}>Annual</span>
                  {billingCycle === 'annual' && <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-1.5 py-0.5">-20%</span>}
                </div>
                <div className="border border-zinc-200 p-4">
                  <div className="flex items-baseline justify-between mb-3">
                    <div>
                      <span className="font-heading font-bold text-lg text-zinc-900">Pro</span>
                      <span className="font-body text-sm text-zinc-500 ml-2">{billingCycle === 'annual' ? '$8/mo ($96/yr)' : '$10/month'}</span>
                    </div>
                  </div>
                  <ul className="space-y-1.5 mb-4">
                    {['50 scans/month', 'Full risk analysis', 'Priority processing'].map((f, i) => (
                      <li key={i} className="flex items-start gap-2 font-body text-xs text-zinc-700"><Check className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" strokeWidth={2} />{f}</li>
                    ))}
                  </ul>
                  <button onClick={() => handleUpgrade(billingCycle === 'annual' ? 'pro_annual' : 'pro')} disabled={upgradeLoading} className="w-full bg-zinc-900 text-white hover:bg-zinc-800 py-2.5 font-body font-medium text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50" data-testid="upgrade-to-pro-btn">
                    {upgradeLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</> : 'Upgrade to Pro'}
                  </button>
                </div>
                <div className="border-2 border-brand p-4 relative">
                  <div className="absolute -top-2.5 left-4 bg-brand text-white px-2 py-0.5 text-xs font-bold tracking-wider uppercase font-body">Best Value</div>
                  <div className="flex items-baseline justify-between mb-3">
                    <div>
                      <span className="font-heading font-bold text-lg text-zinc-900">Pro Plus</span>
                      <span className="font-body text-sm text-zinc-500 ml-2">{billingCycle === 'annual' ? '$12/mo ($144/yr)' : '$15/month'}</span>
                    </div>
                  </div>
                  <ul className="space-y-1.5 mb-4">
                    {['Everything in Pro', 'Contract comparison mode', 'AI negotiation emails', 'PDF report downloads'].map((f, i) => (
                      <li key={i} className="flex items-start gap-2 font-body text-xs text-zinc-700"><Check className="w-3 h-3 text-brand mt-0.5 shrink-0" strokeWidth={2} />{f}</li>
                    ))}
                  </ul>
                  <button onClick={() => handleUpgrade(billingCycle === 'annual' ? 'pro_plus_annual' : 'pro_plus')} disabled={upgradeLoading} className="w-full bg-brand text-white hover:bg-brand-hover py-2.5 font-body font-medium text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50" data-testid="upgrade-to-pro-plus-btn">
                    {upgradeLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</> : 'Upgrade to Pro Plus'}
                  </button>
                </div>
              </div>
            ) : isPro && !isProPlus ? (
              <div className="space-y-4 mb-4">
                <div className="border border-zinc-200 p-4">
                  <p className="font-body text-sm text-zinc-600 mb-3">You're on the Pro plan with 50 scans per month.</p>
                  <button onClick={handleManageBilling} className="w-full border border-zinc-200 py-2 font-body text-sm text-zinc-600 hover:bg-zinc-50 transition-colors" data-testid="manage-billing-btn">Manage Billing</button>
                </div>
                <div className="border-2 border-brand p-4 relative">
                  <div className="absolute -top-2.5 left-4 bg-brand text-white px-2 py-0.5 text-xs font-bold tracking-wider uppercase font-body">Upgrade</div>
                  <div className="flex items-baseline justify-between mb-3">
                    <div>
                      <span className="font-heading font-bold text-lg text-zinc-900">Pro Plus</span>
                      <span className="font-body text-sm text-zinc-500 ml-2">$15/month</span>
                    </div>
                  </div>
                  <ul className="space-y-1.5 mb-4">
                    {['Contract comparison mode', 'AI negotiation emails', 'Side-by-side risk diff'].map((f, i) => (
                      <li key={i} className="flex items-start gap-2 font-body text-xs text-zinc-700"><Check className="w-3 h-3 text-brand mt-0.5 shrink-0" strokeWidth={2} />{f}</li>
                    ))}
                  </ul>
                  <button onClick={() => handleUpgrade('pro_plus')} disabled={upgradeLoading} className="w-full bg-brand text-white hover:bg-brand-hover py-2.5 font-body font-medium text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50" data-testid="upgrade-to-pro-plus-btn">
                    {upgradeLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</> : 'Upgrade to Pro Plus'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="border border-zinc-200 p-4 mb-4">
                <p className="font-body text-sm text-zinc-600 mb-1">You're on the <strong>Pro Plus</strong> plan with all features unlocked.</p>
                <p className="font-body text-xs text-zinc-400 mb-3">50 scans/month, contract comparison, AI negotiation emails</p>
                <button onClick={handleManageBilling} className="w-full border border-zinc-200 py-2 font-body text-sm text-zinc-600 hover:bg-zinc-50 transition-colors" data-testid="manage-billing-btn">Manage Billing</button>
              </div>
            )}
          </div>

          {/* Account Card */}
          <div className="bg-white border border-zinc-200 p-6" data-testid="account-card">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-4 h-4 text-zinc-500" />
              <h2 className="font-heading font-bold text-base text-zinc-900">Account</h2>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-zinc-100">
                <span className="font-body text-sm text-zinc-500">Email</span>
                <span className="font-body text-sm text-zinc-900">{user?.email}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-zinc-100">
                <span className="font-body text-sm text-zinc-500">Name</span>
                <span className="font-body text-sm text-zinc-900">{user?.name}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-zinc-100">
                <span className="font-body text-sm text-zinc-500">Member Since</span>
                <span className="font-body text-sm text-zinc-900">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="bg-amber-50 border border-amber-200 px-4 py-3 mt-6" data-testid="settings-disclaimer">
            <p className="font-body text-xs text-amber-800">
              ContractEye provides risk insights only and does not constitute legal advice.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
